import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import SpeedIcon from '@material-ui/icons/Speed';
import TranslateIcon from '@material-ui/icons/Translate';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import GroupIcon from '@material-ui/icons/Group';
import { SvgIconProps } from '@material-ui/core/SvgIcon';

import authStore from '~/stores/authStore';

const useStyles = makeStyles(({ spacing }) =>
    createStyles({
        root: {
            paddingTop: spacing(4),
            paddingBottom: spacing(6)
        }
    })
);

type Feature = { icon: React.ComponentType<SvgIconProps>; title: string; text: string };
type LangEntry = { sectionTitle: string; features: Feature[] };

const langLib: Record<'en' | 'ru', LangEntry> = {
    en: {
        sectionTitle: 'WHY CONNECT SPEECH API',
        features: [
            {
                icon: SpeedIcon,
                title: 'Fast',
                text: 'Transcription completes faster than real time — no waiting for the full recording before getting results.'
            },
            {
                icon: TranslateIcon,
                title: 'Russian & English',
                text: 'Optimised for Russian business speech including professional vocabulary and regional accents. English fully supported.'
            },
            {
                icon: MonetizationOnIcon,
                title: 'Pay per minute',
                text: 'Only $0.003 per minute of audio. No monthly fees — top up your balance and spend as you go.'
            },
            {
                icon: GroupIcon,
                title: 'Speaker detection',
                text: 'Automatically identifies who said what. Each segment is labelled by speaker, making it easy to analyse dialogues.'
            }
        ]
    },
    ru: {
        sectionTitle: 'ПОЧЕМУ CONNECT SPEECH API',
        features: [
            {
                icon: SpeedIcon,
                title: 'Быстро',
                text: 'Расшифровка завершается быстрее реального времени — не нужно ждать окончания записи.'
            },
            {
                icon: TranslateIcon,
                title: 'Русский и английский',
                text: 'Оптимизировано под русскую деловую речь: бизнес-лексика, акценты, разговорный стиль. Поддерживается также английский язык.'
            },
            {
                icon: MonetizationOnIcon,
                title: 'Поминутная оплата',
                text: 'Всего 0,19 ₽ за минуту аудио. Никаких месячных подписок — пополняйте баланс и тратьте по мере необходимости.'
            },
            {
                icon: GroupIcon,
                title: 'Определение спикеров',
                text: 'Автоматически определяет, кто что говорил. Каждая фраза помечена меткой спикера — удобно для анализа диалогов.'
            }
        ]
    }
};

const SpeechFeatures = () => {
    const classes = useStyles();
    const { lang } = authStore;
    const lib = langLib[lang];

    return (
        <Container maxWidth="md">
            <div className={classes.root}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography variant="h5" component="h2" align="center">
                            {lib['sectionTitle']}
                        </Typography>
                    </Grid>
                    {lib['features'].map((feature, i) => {
                        const Icon = feature.icon;
                        return (
                            <Grid item xs={12} md={6} key={i}>
                                <Card>
                                    <CardHeader
                                        avatar={<Icon fontSize="large" color="secondary" />}
                                        title={feature.title}
                                    />
                                    <CardContent>
                                        <Typography variant="body2" color="textSecondary">
                                            {feature.text}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </div>
        </Container>
    );
};

export default observer(SpeechFeatures);
